import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import useClassy from '@core/hooks/useClassy';
import useIsDocumentReady from '@core/hooks/useIsDocumentReady';
import useUniqueId from '@core/hooks/useUniqueId';

import Modal, { ModalBody } from '@ui/Modal';
import Spinner from '@ui/Spinner';
import Truncate from '@ui/Truncate';

import { useBlockingModalContext } from './Context';
import styles from './index.module.scss';

/**
 * Renders a blocking non-dismissable modal that should be rendered by
 * `<BlockingModalProvider>`. This modal is controlled by the
 * `useBlockingModalContext()` hook.
 */
export default function BlockingModal() {
  const bem = useClassy(styles, 'BlockingModal');
  const uid = useUniqueId('BlockingModal');
  const modalContainerId = uid('modal');
  const modalRef = useRef<Modal>(null);

  const { blockingModalValue } = useBlockingModalContext();
  const isClient = useIsDocumentReady();

  useEffect(() => {
    modalRef.current?.toggle(!!blockingModalValue);
  }, [blockingModalValue]);

  return (
    <>
      <Modal
        ref={modalRef}
        className={bem('-container')}
        noDismiss
        size="sm"
        target={`#${modalContainerId}`}
        verticalCenter
      >
        <ModalBody className={bem('-body')}>
          <Spinner size="md" />
          <Truncate>{blockingModalValue}</Truncate>
        </ModalBody>
      </Modal>

      {isClient
        ? createPortal(
            <div
              className={bem('&', 'ModalWrapper')}
              data-color-mode="dark"
              data-testid={bem()}
              id={modalContainerId}
            />,
            document.body,
          )
        : null}
    </>
  );
}

export * from './Context';
